import React from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { formatDate, TimeFormats } from '../../../lib/DateFormat';
import { Event } from '../EventListDetails.types';

type EventTileDateProps = {
  event: Pick<Event, 'eventStartDate' | 'eventEndDate' | 'multiDayEvent' | 'allDayEvent'>;
};

const TIME_RANGE_SEPARATOR = '–';

/**
 * Date lines of an event tile: the day line on top, the time line below,
 * e.g. "Mi. 15. Juli 2026" / "09:00 – 16:00 Uhr" (or "ganztägig" for all-day events).
 * Every date and time is wrapped in its own <time> element carrying a single
 * machine-readable value, so date ranges stay valid HTML.
 */
export function EventTileDate({ event }: EventTileDateProps) {
  const { t } = useSonTranslation('EventList');

  if (!event.eventStartDate) {
    return null;
  }

  const date = (value: string) => formatDate(value, TimeFormats.SHORT_WEEKDAY_DAY_MONTH_YEAR);
  const isoDate = (value: string) => formatDate(value, TimeFormats.ISO_DATE) ?? undefined;
  const time = (value: string) => formatDate(value, TimeFormats.HOUR_MINUTE) ?? undefined;
  const separator = TIME_RANGE_SEPARATOR;

  return (
    <p className="event-list-tiles__date font-regular-13-13">
      <dfn className="event-list-tiles__date-label font-bold-13-13">{t('tileView.dateLabel')}</dfn>
      <span className="event-list-tiles__date-value font-regular-13-16">
        <span className="event-list-tiles__date-day">
          {event.multiDayEvent ? (
            <>
              <time dateTime={isoDate(event.eventStartDate)}>{date(event.eventStartDate)}</time> {separator}{' '}
              <time dateTime={isoDate(event.eventEndDate)}>{date(event.eventEndDate)}</time>,
            </>
          ) : (
            <>
              <time dateTime={isoDate(event.eventStartDate)}>{date(event.eventStartDate)}</time>,
            </>
          )}
        </span>
        <span className="event-list-tiles__date-time">
          {event.allDayEvent ? (
            t('tileView.allDay')
          ) : (
            <>
              <time dateTime={time(event.eventStartDate)}>{time(event.eventStartDate)}</time> {separator}{' '}
              <time dateTime={time(event.eventEndDate)}>{time(event.eventEndDate)}</time> {t('tileView.oClock')}
            </>
          )}
        </span>
      </span>
    </p>
  );
}
